import React, { useState } from "react";
import { Users, UserCheck, Clock, ArrowUpDown, Mail, UserX, UserCog, RefreshCw, Filter } from "lucide-react";
import { User } from "../../../../types/features/administration/User";
import useToken from "../../../../hooks/auth/useToken";
import useUsers from "../../../../hooks/administration/users/useUsers";
import { usersService } from "../../../../services/administration/usersService";
import { alertError, alertSuccess, getErrorMessage } from "../../../../helpers/alertError";
import { UsersList } from "../../../../components/features/administration/users/UsersList";
import { UserDetails } from "../../../../components/features/administration/users/UserDetailsModal";
import PromoCodeStatsModal from "../../../../components/features/administration/users/PromoCodeStatsModal";
import UsersTransactionModal from "../../../../components/features/administration/users/UsersTrnsactionsModal";
import { confirmModal } from "../../../../helpers/confirmModal";

// Types
interface FilterState {
  search: string;
  kycStatus: string;
  country: string;
  riskLevel: string;
  userStatus: string;
  minTransactionCount?: string;
  minTransactionVolume?: string;
}

type SortOption = "name-asc" | "name-desc" | "email-asc" | "email-desc" | "date-asc" | "date-desc" | "none";
type TransactionSortOption = "none" | "frequency-asc" | "frequency-desc" | "volume-asc" | "volume-desc";
type UserCategory = "all" | "active" | "inactive" | "pending_kyc" | "no_kyc_request" | "approved_kyc" | "rejected_kyc";

// Configuration des tabs
const TAB_CONFIG = {
  all: {
    key: "all" as UserCategory,
    label: "Tous les utilisateurs",
    icon: Users,
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    borderColor: "border-blue-200",
    activeBg: "bg-blue-600",
    activeText: "text-white",
  },
  active: {
    key: "active" as UserCategory,
    label: "Utilisateurs actifs",
    icon: UserCheck,
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    borderColor: "border-green-200",
    activeBg: "bg-green-600",
    activeText: "text-white",
  },
  inactive: {
    key: "inactive" as UserCategory,
    label: "Utilisateurs inactifs",
    icon: UserX,
    bgColor: "bg-gray-50",
    textColor: "text-gray-700",
    borderColor: "border-gray-200",
    activeBg: "bg-gray-600",
    activeText: "text-white",
  },
  pending_kyc: {
    key: "pending_kyc" as UserCategory,
    label: "KYC en attente",
    icon: Clock,
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    borderColor: "border-orange-200",
    activeBg: "bg-orange-600",
    activeText: "text-white",
  },
  no_kyc_request: {
    key: "no_kyc_request" as UserCategory,
    label: "Sans demande KYC",
    icon: UserCog,
    bgColor: "bg-yellow-50",
    textColor: "text-yellow-700",
    borderColor: "border-yellow-200",
    activeBg: "bg-yellow-600",
    activeText: "text-white",
  },
  approved_kyc: {
    key: "approved_kyc" as UserCategory,
    label: "KYC validé",
    icon: UserCheck,
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    borderColor: "border-green-200",
    activeBg: "bg-green-600",
    activeText: "text-white",
  },
  rejected_kyc: {
    key: "rejected_kyc" as UserCategory,
    label: "KYC rejeté",
    icon: UserX,
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    borderColor: "border-red-200",
    activeBg: "bg-red-600",
    activeText: "text-white",
  },
};

// Helper function to map country_id to country info
const getCountryInfo = (countryId: number): { name: string; flag: string } => {
  const countryMap: Record<number, { name: string; flag: string }> = {
    1: { name: "Canada", flag: "🇨🇦" },
    2: { name: "Cameroun", flag: "🇨🇲" },
  };
  return countryMap[countryId] || { name: "Inconnu", flag: "🌍" };
};

// Helper function to determine KYC status based on user data
const getKycStatus = (user: any): "pending" | "approved" | "rejected" | "incomplete" | "not_requested" => {
  if (user.is_verified === "YES") {
    return "approved";
  }
  if (user.is_verified === "PENDING") {
    return "pending";
  }
  if (user.is_verified === "REJECTED") {
    return "rejected";
  }
  if (user.is_verified === "NO" && user.kyc_status === "INACTIVE" && user.has_kyc) {
    return "pending";
  }
  if (user.kyc_status === "NO_KYC" || !user.has_kyc) {
    return "not_requested";
  }
  return "not_requested";
};

// Helper function to determine risk level
const getRiskLevel = (_user: any): "low" | "medium" | "high" => {
  return "low";
};

// Helper function to calculate last activity
const getLastActivity = (updatedAt: string): string => {
  const now = new Date();
  const updated = new Date(updatedAt);
  const diffMs = now.getTime() - updated.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// Helper function to categorize users
const categorizeUsers = (users: User[]): Record<UserCategory, User[]> => {
  return {
    all: users,
    active: users.filter((user) => user.status === "ACTIVE"),
    inactive: users.filter((user) => user.status === "INACTIVE"),
    pending_kyc: users.filter((user) => user.kycStatus === "pending" || user.kycStatus === "incomplete"),
    no_kyc_request: users.filter((user) => user.kycStatus === "not_requested"),
    approved_kyc: users.filter((user) => user.kycStatus === "approved"),
    rejected_kyc: users.filter((user) => user.kycStatus === "rejected"),
  };
};

// Main UsersPage Component
const UsersPage: React.FC = () => {
  const { users: apiUsers, usersLoading, getUsers: refreshUsers } = useUsers({});
  const { token } = useToken();

  // Transform API data to match the User interface
  const allUsers: User[] = apiUsers.map((user) => {
    const countryInfo = getCountryInfo(user.country_id!);

    return {
      id: user.id,
      name: `${user.first_name} ${user.last_name}`,
      email: user.email,
      country: countryInfo.name,
      first_name: user.first_name,
      last_name: user.last_name,
      countryFlag: countryInfo.flag,
      kycStatus: getKycStatus(user),
      riskLevel: getRiskLevel(user),
      interact_email: user.interact_email,
      registrationDate: user?.created_at?.split("T")[0],
      documents: {
        id: false,
        passport: false,
        address: false,
        selfie: false,
      },
      lastActivity: getLastActivity(user?.updated_at || ""),
      status: user.status as "ACTIVE" | "INACTIVE",
      phonenumber: user.phonenumber,
      is_verified: user.is_verified as "YES" | "NO" | "PENDING" | "REJECTED",
      connexion_type: user.connexion_type as "EMAIL" | "GOOGLE" | "FACEBOOK" | "PHONE",
      role: user.role as "USER" | "ADMIN" | "MODERATOR",
      country_id: user.country_id,
      created_at: user.created_at,
      updated_at: user.updated_at,
      kyc_status: user.kyc_status,
      has_kyc: user.has_kyc,
      birthdate: user.birthdate,
      expired_document_date: user.expired_document_date,
      transaction_stats: user.transaction_stats,
    };
  });

  // Catégoriser les utilisateurs
  const userCategories = categorizeUsers(allUsers);

  // State management
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    kycStatus: "",
    country: "",
    riskLevel: "",
    userStatus: "all",
    minTransactionCount: "",
    minTransactionVolume: "",
  });

  const [sortBy, setSortBy] = useState<SortOption>("none");
  const [transactionSort, setTransactionSort] = useState<TransactionSortOption>("none");
  const [showTransactionFilters, setShowTransactionFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedUserForDetails, setSelectedUserForDetails] = useState<User | null>(null);
  const [selectedUserFormTransactions, setSelectedUserForTransactions] = useState<User | null>(null);
  const [selectedUserForPromoCode, setSelectedUserForPromoCode] = useState<User | null>(null);
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const usersPerPage = 10;

  // Filter logic with transaction filters
  const filteredUsers = allUsers.filter((user) => {
    const matchesSearch =
      filters.search === "" ||
      user.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      user.email.toLowerCase().includes(filters.search.toLowerCase());

    const matchesKycStatus = filters.kycStatus === "" || user.kycStatus === filters.kycStatus;
    const matchesCountry = filters.country === "" || user.country === filters.country;
    const matchesRiskLevel = filters.riskLevel === "" || user.riskLevel === filters.riskLevel;

    const matchesUserStatus =
      filters.userStatus === "all" ||
      (filters.userStatus === "active" && user.status === "ACTIVE") ||
      (filters.userStatus === "inactive" && user.status === "INACTIVE") ||
      (filters.userStatus === "pending_kyc" && (user.kycStatus === "pending" || user.kycStatus === "incomplete")) ||
      (filters.userStatus === "no_kyc_request" && user.kycStatus === "not_requested") ||
      (filters.userStatus === "approved_kyc" && user.kycStatus === "approved") ||
      (filters.userStatus === "rejected_kyc" && user.kycStatus === "rejected");
    // Transaction filters
    const matchesMinTransactions =
      !filters.minTransactionCount ||
      (user.transaction_stats?.transaction_count || 0) >= parseInt(filters.minTransactionCount);

    const matchesMinVolume =
      !filters.minTransactionVolume ||
      (user.transaction_stats?.total_sent || 0) >= parseFloat(filters.minTransactionVolume);

    return (
      matchesSearch &&
      matchesKycStatus &&
      matchesCountry &&
      matchesRiskLevel &&
      matchesUserStatus &&
      matchesMinTransactions &&
      matchesMinVolume
    );
  });

  // Sort logic - Priorité au tri par transactions si sélectionné
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    // Si un tri par transactions est sélectionné, on l'applique en priorité
    if (transactionSort !== "none") {
      switch (transactionSort) {
        case "frequency-desc":
          return (b.transaction_stats?.transaction_count || 0) - (a.transaction_stats?.transaction_count || 0);
        case "frequency-asc":
          return (a.transaction_stats?.transaction_count || 0) - (b.transaction_stats?.transaction_count || 0);
        case "volume-desc":
          return (b.transaction_stats?.total_sent || 0) - (a.transaction_stats?.total_sent || 0);
        case "volume-asc":
          return (a.transaction_stats?.total_sent || 0) - (b.transaction_stats?.total_sent || 0);
        default:
          return 0;
      }
    }

    // Sinon on applique le tri standard
    switch (sortBy) {
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "email-asc":
        return a.email.localeCompare(b.email);
      case "email-desc":
        return b.email.localeCompare(a.email);
      case "date-asc":
        return new Date(a.registrationDate).getTime() - new Date(b.registrationDate).getTime();
      case "date-desc":
        return new Date(b.registrationDate).getTime() - new Date(a.registrationDate).getTime();
      default:
        return 0;
    }
  });

  // Reset transaction filters
  const resetTransactionFilters = () => {
    setFilters({
      ...filters,
      minTransactionCount: "",
      minTransactionVolume: "",
    });
  };

  // Pagination calculations
  const totalPages = Math.ceil(sortedUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = Math.min(startIndex + usersPerPage, sortedUsers.length);

  // Fonction pour envoyer les rappels KYC
  const handleSendKycReminders = async () => {
    setIsSendingReminders(true);
    try {
      const usersWithPendingKyc = userCategories.pending_kyc;
      const response = await usersService.remindNoKyC(token);
      alertSuccess(
        response?.message || `✅ Rappels KYC envoyés avec succès à ${usersWithPendingKyc.length} utilisateurs`
      );
    } catch (error) {
      console.error("Erreur lors de l'envoi des rappels:", error);
      alert("❌ Erreur lors de l'envoi des rappels KYC");
    } finally {
      setIsSendingReminders(false);
    }
  };

  // Event handlers
  const handleSelectAll = (checked: boolean) => {
    const currentUsers = sortedUsers.slice(startIndex, startIndex + usersPerPage);
    if (checked) {
      setSelectedUsers(currentUsers.map((user) => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    }
  };

  const handleUserAction = (action: string, user: User) => {
    if (action === "view") {
      setSelectedUserForDetails(user);
    }

    if (action === "promo-code") {
      setSelectedUserForPromoCode(user);
    }
    if (action === "transaction-history") {
      setSelectedUserForTransactions(user);
    }
    if (action === "approve") {
      handleApprove(user);
    }
    if (action === "reject") {
      handleReject(user);
    }
  };

  const handleApprove = async (user: User) => {
    const confirmed = await confirmModal(`Voulez vous approuver le compte de ${user.name}`);
    if (!confirmed) {
      return;
    }
    try {
      await usersService.approveUserKYC(user.id, token);
      setSelectedUserForDetails(null);
      refreshUsers();
    } catch (error: any) {
      alertError(`Une erreur s'est produit: ${error.response.data.detail}`);
    }
  };

  const handleReject = async (user: User) => {
    const confirmed = await confirmModal(`Voulez vous rejeter le compte de ${user.name}`);
    if (!confirmed) {
      return;
    }
    try {
      await usersService.rejectUserKYC(user.id, token);
      setSelectedUserForDetails(null);
      refreshUsers();
    } catch (error: any) {
      console.log("error", error.response);
      alertError(`Une erreur s'est produit: ${error.response.data.detail}`);
    }
  };

  const handleTabChange = (tabKey: UserCategory) => {
    setFilters((prev) => ({ ...prev, userStatus: tabKey }));
    setCurrentPage(1);
  };

  const isRequiredKYC = userCategories.pending_kyc.length === 0 && userCategories.no_kyc_request.length === 0;
  const usersToSendKYC = userCategories.pending_kyc.length + userCategories.no_kyc_request.length;

  return (
    <div>
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-800">Gestion des Utilisateurs</h1>
        <p className="text-gray-600">Gestion KYC et validation des comptes utilisateurs</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-6">
        {Object.entries(TAB_CONFIG).map(([key, config]) => {
          const IconComponent = config.icon;
          const count = userCategories[key as UserCategory].length;

          return (
            <div key={key} className="p-4 bg-white rounded-lg border border-gray-200 transition-shadow hover:shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">{config.label.split(" (")[0]}</p>
                  <p className={`text-xl font-bold ${config.textColor}`}>{count}</p>
                </div>
                <IconComponent className={`w-6 h-6 ${config.textColor}`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs pour les filtres par statut */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto -mb-px">
            {Object.entries(TAB_CONFIG).map(([key, config]) => {
              const IconComponent = config.icon;
              const count = userCategories[key as UserCategory].length;
              const isActive = filters.userStatus === key;

              return (
                <button
                  key={key}
                  onClick={() => handleTabChange(key as UserCategory)}
                  className={`
                    flex items-center space-x-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors
                    ${
                      isActive
                        ? `${config.activeBg} ${config.activeText} border-current`
                        : `border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300`
                    }
                    ${key === "all" ? "rounded-tl-lg" : ""}
                    ${key === "approved_kyc" ? "rounded-tr-lg" : ""}
                  `}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{config.label}</span>
                  <span
                    className={`
                    px-2 py-1 rounded-full text-xs font-medium
                    ${isActive ? "bg-white bg-opacity-20" : config.bgColor + " " + config.textColor}
                  `}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Contenu des tabs - Indicateur de filtre actif */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Filtre actif :</span>
              <span
                className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full 
                text-sm font-medium ${TAB_CONFIG[filters.userStatus as UserCategory]?.bgColor} 
                ${TAB_CONFIG[filters.userStatus as UserCategory]?.textColor}`}
              >
                <span>{TAB_CONFIG[filters.userStatus as UserCategory]?.label}</span>
                <span className="ml-1">• {userCategories[filters.userStatus as UserCategory].length}</span>
              </span>
            </div>

            {filters.userStatus !== "all" && (
              <button
                onClick={() => handleTabChange("all")}
                className="text-sm text-gray-500 underline transition-colors hover:text-gray-700"
              >
                Voir tous les utilisateurs
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Header avec tri et bouton rappel KYC */}
      <div className="flex flex-col gap-4 justify-between items-start mb-6 sm:flex-row sm:items-center">
        {/* Sort Options */}
        <div className="flex-1 p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex gap-4 items-center flex-wrap">
            <ArrowUpDown className="w-5 h-5 text-gray-600" />
            <label className="text-sm font-medium text-gray-700">Trier par:</label>

            {/* Select pour le tri standard */}
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as SortOption);
                setTransactionSort("none");
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="none">Par défaut</option>
              <option value="name-asc">Nom (A → Z)</option>
              <option value="name-desc">Nom (Z → A)</option>
              <option value="email-asc">Email (A → Z)</option>
              <option value="email-desc">Email (Z → A)</option>
              <option value="date-asc">Date d'inscription (Plus ancien)</option>
              <option value="date-desc">Date d'inscription (Plus récent)</option>
            </select>

            {/* Select pour le tri par transactions */}
            <select
              value={transactionSort}
              onChange={(e) => {
                setTransactionSort(e.target.value as TransactionSortOption);
                if (e.target.value !== "none") {
                  setSortBy("none");
                }
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="none">Trier par transactions</option>
              <option value="frequency-desc">Fréquence (Plus élevée → Plus basse)</option>
              <option value="frequency-asc">Fréquence (Plus basse → Plus élevée)</option>
              <option value="volume-desc">Volume (Plus élevé → Plus bas)</option>
              <option value="volume-asc">Volume (Plus bas → Plus élevé)</option>
            </select>

            {/* Bouton pour afficher/masquer les filtres transactionnels */}
            <button
              onClick={() => setShowTransactionFilters(!showTransactionFilters)}
              className="flex gap-2 items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
            >
              <Filter className="w-4 h-4" />
              Filtres transactions
            </button>

            {(sortBy !== "none" || transactionSort !== "none") && (
              <button
                onClick={() => {
                  setSortBy("none");
                  setTransactionSort("none");
                }}
                className="text-sm text-gray-600 underline hover:text-gray-800"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-2 items-center">
          {/* Bouton Rafraîchir */}
          <button
            onClick={refreshUsers}
            disabled={usersLoading}
            className="flex gap-2 items-center px-4 py-2 text-white whitespace-nowrap bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${usersLoading ? "animate-spin" : ""}`} />
            Rafraîchir
          </button>

          {/* Bouton Rappel KYC */}
          <button
            onClick={handleSendKycReminders}
            disabled={isSendingReminders || isRequiredKYC}
            className="flex gap-2 items-center px-4 py-2 text-white whitespace-nowrap bg-orange-600 rounded-lg transition-colors hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSendingReminders ? (
              <>
                <div className="w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                Envoi en cours...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Envoyer rappel KYC ({usersToSendKYC})
              </>
            )}
          </button>
        </div>
      </div>

      {/* Transaction Filters Panel */}
      {showTransactionFilters && (
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Filtres transactionnels</h3>
            <button onClick={resetTransactionFilters} className="text-sm text-red-600 hover:text-red-700">
              Réinitialiser
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre minimum de transactions</label>
              <input
                type="number"
                value={filters.minTransactionCount || ""}
                onChange={(e) => setFilters({ ...filters, minTransactionCount: e.target.value })}
                placeholder="Ex: 10"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Volume minimum (CAD)</label>
              <input
                type="number"
                value={filters.minTransactionVolume || ""}
                onChange={(e) => setFilters({ ...filters, minTransactionVolume: e.target.value })}
                placeholder="Ex: 1000"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          {(filters.minTransactionCount || filters.minTransactionVolume) && (
            <div className="mt-3 text-xs text-gray-500">
              ⚡ Actif: {filters.minTransactionCount && `≥ ${filters.minTransactionCount} transactions`}
              {filters.minTransactionCount && filters.minTransactionVolume && " et "}
              {filters.minTransactionVolume && `≥ ${filters.minTransactionVolume} CAD`}
            </div>
          )}
        </div>
      )}

      {/* Users List */}
      <UsersList
        users={sortedUsers}
        filters={filters}
        onFiltersChange={setFilters}
        selectedUsers={selectedUsers}
        onSelectUser={handleSelectUser}
        onSelectAll={handleSelectAll}
        onUserAction={handleUserAction}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        startIndex={startIndex}
        endIndex={endIndex}
        totalUsers={sortedUsers.length}
        loading={usersLoading}
      />

      {/* User Details Modal */}
      <UserDetails
        user={selectedUserForDetails}
        onClose={() => setSelectedUserForDetails(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        onUserUpdate={refreshUsers}
      />

      {selectedUserForPromoCode && (
        <PromoCodeStatsModal
          user={selectedUserForPromoCode}
          onClose={() => {
            setSelectedUserForPromoCode(null);
          }}
        />
      )}

      {selectedUserFormTransactions && (
        <UsersTransactionModal
          isOpen={!!selectedUserFormTransactions}
          userId={selectedUserFormTransactions.id}
          onClose={() => {
            setSelectedUserForTransactions(null);
          }}
        />
      )}
    </div>
  );
};

export default UsersPage;
